import { ElMessageBox } from 'element-plus'
import { UAS_APP_URL } from '~/constants'

export type UtasOnlyOrgOutcome = 'redirect-to-utas' | 'logout'

/**
 * Full-page navigation to the dedicated UTAS app. Separate so callers/tests can
 * reason about "we are leaving for UTAS" without stubbing `window.location`.
 */
export function goToUtasApp() {
  window.location.href = UAS_APP_URL
}

/**
 * Decide what to do when a user lands on a UTAS-only org in the main app.
 *
 * UTAS-only orgs live in the dedicated UTAS app, so the main app sends users there.
 * But because the two apps are on separate origins (separate localStorage), a user
 * who has *other* orgs can get stuck: the stale UTAS org keeps bouncing them to UTAS
 * with no way to switch back. So:
 *
 * - No other org to return to → they belong on UTAS → redirect silently (unchanged
 *   behavior for dedicated UTAS customers).
 * - Has another org → offer a choice. "Continue to UTAS", or "Log out" — logging out
 *   clears the stale UTAS auth so they can log back into a different org instead of
 *   being trapped.
 */
export async function resolveUtasOnlyOrgLanding(
  hasNonUtasOrg: boolean
): Promise<UtasOnlyOrgOutcome> {
  if (!hasNonUtasOrg) {
    return 'redirect-to-utas'
  }
  try {
    await ElMessageBox.confirm(
      'Your current organization is a UTAS organization, which lives in the UTAS app. ' +
        'To switch to a different organization, log out and back in.',
      'UTAS Organization',
      {
        confirmButtonText: 'Continue to UTAS',
        cancelButtonText: 'Log out',
        type: 'warning',
        closeOnClickModal: false,
        closeOnPressEscape: false,
        showClose: false,
      }
    )
    return 'redirect-to-utas'
  } catch {
    // Cancel (or any dismissal) means "Log out" — the escape from the trap.
    return 'logout'
  }
}
